import { format, differenceInMinutes } from 'date-fns';
import { ErrorMessageEnum } from '../enums/app.enum';

export const utils = {
  removeSpace,
  validPassword,
  validEmail,
  formatDate,
  formatDateUTC,
  getLatLngCenter,
  formatNumber,
  convertHourMinToInputMaskValue,
  validPickupTime,
  validSingPhoneNumber,
  validatePhoneNumberLength,
  validateOverseasPhoneNumber,
};

const REGEX_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// const REGEX_EMAIL = /^[^\s@]+@[^\s@]+$/
const REGEX_PASSWORD =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d#?!@$ %^&*-]{8,}$/;
const REGEX_SING_PHONE = /\+65(6|8|9)\d{7}/g;
const REGEX_OVERSEA_PHONE = /^((\+)?[1-9]{1,2})(([-\s\.])?[0-9]{9,14})$/;

function validPassword(password: string) {
  return !!password.match(REGEX_PASSWORD);
}

function validEmail(email: string) {
  const input = String(email).toLowerCase();
  return !!input.match(REGEX_EMAIL);
}

/**
 * convert date|string to format date string
 * @param d any
 * @param formatString 'dd/MM/yyyy'
 */
function formatDateUTC(d: string | undefined) {
  try {
    return d ? d.split("T")[0] + " " + d.split("T")[1].substr(0, 8) : '';
  } catch (err) {
    return '';
  }
}

function formatDate(d: string | Date | undefined, formatString: string) {
  try {
    return d ? format(new Date(d), formatString) : '';
  } catch (err) {
    return '';
  }
}

function removeSpace(d: string) {
  try {
    return d ? d.replace(/\s/g, '') : '';
  } catch (err) {
    return '';
  }
}

function rad2degr(rad: number) {
  return (rad * 180) / Math.PI;
}
function degr2rad(degr: number) {
  return (degr * Math.PI) / 180;
}

/**
 * @param latLngInDeg array of arrays with latitude and longtitude
 *  pairs in degrees. e.g.
 *  [
 *    { lat: 1.352083, lng: 103.819839 },
 *    { lat: 1.352083, lng: 103.819839 },
 *    ...
 *  ]
 *
 * @return array with the center latitude longtitude pairs in
 *   degrees.
 */
function getLatLngCenter(latLngInDegr: any[]) {
  var sumX = 0;
  var sumY = 0;
  var sumZ = 0;

  for (var i = 0; i < latLngInDegr.length; i++) {
    var lat = degr2rad(+latLngInDegr[i].lat);
    var lng = degr2rad(+latLngInDegr[i].lng);
    // sum of cartesian coordinates
    sumX += Math.cos(lat) * Math.cos(lng);
    sumY += Math.cos(lat) * Math.sin(lng);
    sumZ += Math.sin(lat);
  }

  var avgX = sumX / latLngInDegr.length;
  var avgY = sumY / latLngInDegr.length;
  var avgZ = sumZ / latLngInDegr.length;

  // convert average x, y, z coordinate to latitude and longtitude
  var lng = Math.atan2(avgY, avgX);
  var hyp = Math.sqrt(avgX * avgX + avgY * avgY);
  var lat = Math.atan2(avgZ, hyp);

  return { lat: rad2degr(lat), lng: rad2degr(lng) };
}

function formatNumber(yourNumber: number | undefined | string) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      maximumFractionDigits: 2,
    }).format(Number(yourNumber));
  } catch {
    return yourNumber;
  }
}

function convertHourMinToInputMaskValue(hours: number, mins: number) {
  const strHours = ('00' + hours).substr(-2, 2);
  const strMins = ('00' + mins).substr(-2, 2);
  return `${strHours}:${strMins}`;
}

function validPickupTime(pickupTime: Date, minReserveHour: number) {
  if (differenceInMinutes(pickupTime, new Date()) <= 0) {
    return ErrorMessageEnum.mustGreaterThanToday;
  }

  if (minReserveHour === 0) return true;

  return (
    differenceInMinutes(pickupTime, new Date()) >= minReserveHour * 60 ||
    ErrorMessageEnum.mustBookXHour.replace('{{X}}', minReserveHour + '')
  );
}

function validSingPhoneNumber(phone: string) {
  if (!phone) return false;

  const trimPhone = phone.trim().replace(' ', '');

  return !!trimPhone.match(REGEX_SING_PHONE);
}

function validatePhoneNumberLength(phone: string) {
  if (!phone) return false;

  const trimPhone = phone.trim().replace(' ', '');

  return trimPhone.length <= 10 || trimPhone.length >= 18 ? false : true;
}

function validateOverseasPhoneNumber(phone: string) {
  if (!phone) return false;

  const trimPhone = phone.trim().replace(' ', '');

  return !!trimPhone.match(REGEX_OVERSEA_PHONE);
}

export function uniq(items: any[]) {
  function onlyUnique(value: any, index: any, self: any) {
    return self.indexOf(value) === index;
  }

  return items.filter(onlyUnique);
}